// src/pages/TermsOfServicePage.jsx - Enhanced Black & White Design
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, ArrowLeft, Shield, Mail, Globe } from 'lucide-react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const TermsOfServicePage = () => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();

  // Scroll animations for sections
  const [headerRef, headerVisible] = useScrollAnimation();
  const [contentRef, contentVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  // All sections for staggered animation
  const sections = [
    { key: 'intro', number: null },
    { key: 'acceptance', number: 1 },
    { key: 'service', number: 2 },
    { key: 'accounts', number: 3 },
    { key: 'conduct', number: 4 },
    { key: 'ip', number: 5 },
    { key: 'payments', number: 6 },
    { key: 'liability', number: 7 },
    { key: 'jurisdiction', number: 8 },
    { key: 'changes', number: 9 },
    { key: 'contact', number: 10 }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-12">
        {/* Back Button - Enhanced */}
        <button
          onClick={() => navigate(-1)}
          className="group flex items-center gap-2 text-black/60 hover:text-black mb-8 transition-all duration-300 font-medium"
        >
          <ArrowLeft className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" />
          <span>{t('legal.back')}</span>
          <div className="absolute bottom-0 left-0 h-0.5 bg-black w-0 group-hover:w-full transition-all duration-300" />
        </button>

        {/* Header - Enhanced */}
        <div
          ref={headerRef}
          className={`
            relative bg-white rounded-2xl border-[3px] border-black shadow-2xl p-8 mb-8 overflow-hidden
            transition-all duration-1000
            ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
          `}
        >
          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-black/10 rounded-tl-2xl" />
          <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-black/10 rounded-br-2xl" />

          <div className="relative z-10 flex items-start gap-6">
            {/* Icon */}
            <div className="flex-shrink-0 w-16 h-16 bg-black rounded-2xl flex items-center justify-center shadow-lg">
              <FileText className="w-8 h-8 text-white" />
            </div>

            {/* Title */}
            <div className="flex-1">
              <h1 className="font-space text-3xl sm:text-4xl font-black text-black mb-2 tracking-tight">
                {t('legal.terms.title')}
              </h1>
              <p className="text-black/60 font-medium">
                {t('legal.lastUpdated')}: October 12, 2025
              </p>
              <p className="text-black/60 text-sm mt-2">
                {t('footer.disclaimer')}
              </p>
            </div>
          </div>
        </div>

        {/* Content - Enhanced with staggered animations */}
        <div
          ref={contentRef}
          className="relative bg-white rounded-2xl border-[3px] border-black shadow-2xl p-8 mb-8 overflow-hidden"
        >
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50 opacity-50" />

          <div className="relative z-10 prose prose-lg max-w-none">
            {sections.map((section, index) => {
              const isIntro = section.key === 'intro';
              const hasItems = ['acceptance', 'service', 'accounts', 'conduct', 'payments'].includes(section.key);

              return (
                <section
                  key={section.key}
                  className={`
                    mb-10 pb-8 border-b border-black/10 last:border-b-0
                    transition-all duration-700
                    ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                  `}
                  style={{
                    transitionDelay: contentVisible ? `${index * 0.05}s` : '0s'
                  }}
                >
                  {/* Section Header */}
                  <div className="flex items-start gap-4 mb-4">
                    {section.number && (
                      <div className="flex-shrink-0 w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center font-bold">
                        {section.number}
                      </div>
                    )}
                    <h2 className="font-space text-2xl font-black text-black tracking-tight flex-1">
                      {isIntro ? t(`legal.terms.${section.key}.title`) : t(`legal.terms.${section.key}.title`)}
                    </h2>
                  </div>

                  {/* Section Content */}
                  <div className="space-y-4">
                    <p className="text-black/70 leading-relaxed">
                      {t(`legal.terms.${section.key}.text`) || t(`legal.terms.${section.key}.text1`)}
                    </p>

                    {/* Additional paragraph for intro */}
                    {isIntro && t(`legal.terms.${section.key}.text2`) && (
                      <p className="text-black/70 leading-relaxed">
                        {t(`legal.terms.${section.key}.text2`)}
                      </p>
                    )}

                    {/* List items */}
                    {hasItems && (
                      <ul className="space-y-3 mt-4">
                        {[1, 2, 3, 4, 5].map(itemNum => {
                          const itemKey = `legal.terms.${section.key}.item${itemNum}`;
                          const itemText = t(itemKey);
                          // Only render if translation exists
                          if (itemText !== itemKey) {
                            return (
                              <li key={itemNum} className="flex items-start gap-3 group">
                                <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                                  <span className="text-xs font-bold">✓</span>
                                </div>
                                <span className="text-black/70 leading-relaxed flex-1">
                                  {itemText}
                                </span>
                              </li>
                            );
                          }
                          return null;
                        })}
                      </ul>
                    )}

                    {/* Contact Info Box */}
                    {section.key === 'contact' && (
                      <div className="relative bg-white border-2 border-black/10 rounded-xl p-6 mt-6 group hover:border-black transition-all duration-300">
                        <p className="font-bold text-black mb-4 text-lg">Logami</p>

                        <div className="space-y-3">
                          <div className="flex items-center gap-3 text-black/70 group-hover:text-black transition-colors">
                            <div className="w-8 h-8 bg-black/5 rounded-lg flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all duration-300">
                              <Mail className="w-4 h-4" />
                            </div>
                            <span className="font-medium">team@logami.app</span>
                          </div>

                          <div className="flex items-center gap-3 text-black/70 group-hover:text-black transition-colors">
                            <div className="w-8 h-8 bg-black/5 rounded-lg flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all duration-300">
                              <Globe className="w-4 h-4" />
                            </div>
                            <span className="font-medium">https://logami.app</span>
                          </div>
                        </div>

                        {/* Decorative accent */}
                        <div className="absolute top-3 right-3 w-3 h-3 bg-black/10 rounded-full group-hover:bg-black transition-colors duration-300" />
                      </div>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </div>

        {/* Footer CTA - Enhanced */}
        <div
          ref={ctaRef}
          className={`
            relative bg-white border-[3px] border-black rounded-2xl p-8 text-center overflow-hidden group
            transition-all duration-1000 hover:shadow-2xl
            ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
          `}
        >
          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-black/10 rounded-tl-2xl" />
          <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-black/10 rounded-br-2xl" />

          <div className="relative z-10">
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-2xl mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
              <Shield className="w-8 h-8 text-white" />
            </div>

            <p className="text-lg text-black/70 mb-6 max-w-2xl mx-auto leading-relaxed">
              {t('legal.terms.footer.text')}
            </p>

            <button
              onClick={() => navigate('/contact')}
              className="group/btn relative px-8 py-4 bg-black text-white font-space font-bold rounded-xl border-3 border-black overflow-hidden transition-all duration-500 hover:scale-105 hover:shadow-2xl"
            >
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-black to-gray-800 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500" style={{
                backgroundSize: '200% 100%',
                animation: 'shimmer 3s infinite'
              }} />

              {/* Button content */}
              <span className="relative z-10 flex items-center justify-center gap-2">
                <span>{t('legal.terms.footer.button')}</span>
                <ArrowLeft className="w-5 h-5 rotate-180 transition-transform duration-300 group-hover/btn:translate-x-1" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;